<template>
  <div
      class="dashboard-grid"
      :class="{ 'dashboard-grid--subscription-locked': !hasSmallCardsAccess }"
  >
    <SmallCardWrapper
        class="dashboard-card dashboard-card--summary dashboard-card--energy"
        name="Энергия компании"
        :helpText="'Энергия компании'"
        :locked="!hasSmallCardsAccess"
        :additionalClass="'justify-end'"
    >
      <div class="metric-card metric-card--energy">
        <div class="metric-card__main">
          {{ energy?.total }}%
        </div>

        <div class="metric-card__change">
          <span
              :class="{
              'positive-change': energyChange > 0,
              'negative-change': energyChange < 0
            }"
          >
            {{ energy?.total ? (getLevelLabel(energy?.total, percentLevels) + ":") : '' }}
            {{ energyChangeText }}
          </span>

          <span>за неделю</span>
        </div>

        <RiskProgress :percent="energy?.total ?? 0" />
      </div>
    </SmallCardWrapper>

    <SmallCardWrapper
        class="dashboard-card dashboard-card--summary dashboard-card--company-ei"
        name="Средний ЭИ компании"
        :showHelp="true"
        :helpText="'Средний ЭИ компании'"
        :locked="!hasSmallCardsAccess"
        :additionalClass="'justify-end'"
    >
      <div class="metric-card company-ei-card">
        <div class="average-ei-wrapper">
          <div class="company-ei-card__row">
            <span>Ваша компания</span>
            <span class="ei-card-score">{{ companyTotalScore ?? 0 }}</span>
          </div>

          <a-progress
              :percent="(companyTotalScore ?? 50) - 50"
              :show-info="false"
              stroke-color="#52C41A"
              trail-color="#F2F4F7"
              :stroke-width="7"
              class="metric-card__progress"
              :format="percent => `${percent}%`"
          />

          <div class="company-ei-card__scale">
            <span></span>
            <span>
              {{ companyTotalScore ? getLevelLabel(companyTotalScore, eiLevels) : '' }}
            </span>
          </div>
        </div>

        <div class="average-ei-wrapper">
          <div class="company-ei-card__row">
            <span>Средний по России</span>
            <span class="ei-card-score">{{ avgTotalScore ?? 0 }}</span>
          </div>

          <a-progress
              :percent="(avgTotalScore ?? 50) - 50"
              :show-info="false"
              stroke-color="#8BD05B"
              trail-color="#F2F4F7"
              :stroke-width="7"
              class="metric-card__progress"
              :format="percent => `${percent}%`"
          />

          <div class="company-ei-card__scale">
            <span></span>
            <span>
              {{ avgTotalScore ? getLevelLabel(avgTotalScore, eiLevels) : '' }}
            </span>
          </div>
        </div>
      </div>
    </SmallCardWrapper>

    <SmallCardWrapper
        class="dashboard-card dashboard-card--summary dashboard-card--management"
        name="Управленческий потенциал"
        :helpText="'Управленческий потенциал'"
        :locked="!hasSmallCardsAccess"
        :additionalClass="'justify-end'"
    >
      <div class="metric-card management-card">
        <div class="management-card__legend">
          <div
              v-for="item in managementItems"
              :key="item.key"
              class="management-card__legend-row"
          >
            <div class="management-card__level-wrapper">
              <div
                  class="management-card__mark"
                  :class="'management-card__mark--' + item.color"
              ></div>

              <span>{{ item.label }}</span>
            </div>

            <span
                class="management-card__delta"
                :class="item.deltaClass"
            >
              <span
                  v-if="item.delta !== null"
                  class="management-card__arrow"
              >
                {{ item.deltaArrow }}
              </span>

              <span>
                {{ item.delta !== null ? item.deltaValue : '-' }}
              </span>
            </span>

            <span class="ei-card-score">
              {{ item.value || '-' }}
            </span>
          </div>
        </div>

        <div class="management-card__chart">
          <div
              class="management-card__bar"
              :class="{ 'management-card__bar--empty': !hasManagementSegments }"
          >
            <template v-if="hasManagementSegments">
              <div
                  v-for="item in managementSegments"
                  :key="item.key"
                  class="management-card__bar-part"
                  :class="'management-card__bar-part--' + item.color"
                  :style="{ width: item.percent + '%' }"
              ></div>
            </template>
          </div>

          <div
              v-if="hasManagementSegments"
              class="management-card__numbers"
          >
            <span
                v-for="item in managementSegments"
                :key="item.key"
                class="management-card__number"
                :style="{ width: item.percent + '%' }"
            >
              {{ item.value }}
            </span>
          </div>
        </div>
      </div>
    </SmallCardWrapper>

    <SmallCardWrapper
        class="dashboard-card dashboard-card--small dashboard-card--soft-skills"
        name="Применение гибких навыков"
        :heightType="'small'"
        :helpText="'Применение гибких навыков'"
        :additionalClass="'justify-end'"
    >
      <div class="metric-card">
        <div class="metric-card__main">
          {{ softSkills?.total ?? 0 }}%
        </div>

        <div class="metric-card__change">
          <span
              :class="{
              'positive-change': softSkillsChange > 0,
              'negative-change': softSkillsChange < 0
            }"
          >
            {{ softSkillsText ?? '' }}
          </span>

          <span>за месяц</span>
        </div>
      </div>
    </SmallCardWrapper>

    <SmallCardWrapper
        class="dashboard-card dashboard-card--small dashboard-card--fatigue"
        name="Уровень хронической усталости"
        :heightType="'small'"
        :helpText="'Уровень хронической усталости'"
        :additionalClass="'justify-end'"
    >
      <div class="metric-card fatigue-card">
        <div class="fatigue-card__value">
          <span class="fatigue-current">{{ fatigueScore?.total ?? 0 }}</span>
          <span>/ 10</span>
        </div>

        <div class="metric-card__change">
          <span
              :class="{
              'negative-change': fatigueChangePercent > 0,
              'positive-change': fatigueChangePercent < 0
            }"
          >
            {{ fatigueScore?.total ? (getLevelLabel(fatigueScore?.total, fatigueScoreLevels) + ':') : '' }}
            {{ fatigueChangeText ?? '' }}
          </span>

          <span>за квартал</span>
        </div>
      </div>
    </SmallCardWrapper>

    <div class="dashboard-card dashboard-card--small dashboard-card--users content-block">
      <div class="small-card">
        <div class="status-header">
          <div>
            Пользователи
          </div>

          <div
              class="count"
              :class="{'count-underlined': userStats.total?.count}"
              @click="handleLink(userStats?.total.link, userStats?.total.type)"
          >
            {{ userStats.total?.count ?? '' }}
          </div>
        </div>

        <div class="users-container">
          <div class="users-row">
            <span>
              Активные группы
            </span>

            <span
                class="count"
                :class="{'count-underlined': userStats.group?.count}"
                @click="handleLink(userStats?.group.link, userStats?.group.type)"
            >
              {{ userStats.group?.count ?? '' }}
            </span>
          </div>

          <div class="users-row">
            <span>
              В архиве
            </span>

            <span
                class="count"
                :class="{'count-underlined': userStats.archive?.count}"
                @click="handleLink(userStats?.archive.link, userStats?.archive.type)"
            >
              {{ userStats.archive?.count ?? '' }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <div class="dashboard-card dashboard-card--detail dashboard-card--analytics content-block">
      <div class="status-header">
        <div>
          Топ-5 результатов оценки ЭИ по группам
        </div>

        <RightOutlined
            class="right-arrow-icon"
            @click="router.push('/Analytics/base')"
        />
      </div>

      <div class="analytics-table">
        <div
            v-for="item in dataAnalytics.slice(0, 5)"
            :key="item.group"
            class="progress-row"
        >
          <div class="progress-row__header">
            <span class="progress-row__label">{{ item.group }}</span>
            <span class="progress-row__value">{{ item.average }}</span>
          </div>

          <a-progress
              :percent="(item.average ?? 50) - 50"
              :show-info="false"
              stroke-color="#8BD05B"
              trail-color="#F2F4F7"
              :stroke-width="8"
              class="progress-row__bar"
          />
        </div>
      </div>
    </div>

    <div class="dashboard-card dashboard-card--detail dashboard-card--alignment content-block">
      <div class="status-header">
        <div>
          Совпадения с эталонным профилем
        </div>

        <RightOutlined
            class="right-arrow-icon"
            @click="showDetailedAlignment = true"
        />
      </div>

      <div class="reference-profile-alignment">
        <a-select
            v-model:value="selectedReferenceProfileId"
            :options="referenceProfileOptions"
            placeholder="Выберите эталонный профиль"
            style="width: 100%"
        />

        <div
            v-if="selectedUsers && selectedUsers.length"
            class="alignment-wrapper"
        >
          <div
              v-for="user in selectedUsers.slice(0, 6)"
              :key="user.user_id"
              class="alignment-item"
          >
            <a
                :href="'/staff/' + user.user_id + '/edit'"
                target="_blank"
                class="profile-name"
            >
              {{ user.name }}
            </a>

            <div>{{ user.aligned }}%</div>
          </div>
        </div>
      </div>
    </div>

    <ReferenceProfileAlignmentModal
        :open="showDetailedAlignment"
        :referenceProfileAlignment="referenceProfileAlignment"
        @close="showDetailedAlignment = false"
    />
  </div>
</template>

<script>

import {RightOutlined} from "@ant-design/icons-vue";
import {useRouter} from "vue-router";
import {onBeforeUnmount, ref, watch} from "@vue/runtime-core";
import {computed} from "@vue/reactivity";
import ReferenceProfileAlignmentModal from "@/components/OtherElements/ReferenceProfileAlignmentModal.vue";
import {useStore} from "vuex";
import SmallCardWrapper from "@/components/Widgets/WidgetCard.vue";
import RiskProgress from "@/components/Widgets/RiskBar.vue";
import {onMounted} from "vue";

export default {
  name: "DashboardGrid",
  components: {RiskProgress, SmallCardWrapper, ReferenceProfileAlignmentModal, RightOutlined},
  props: {
    referenceProfileAlignment: {
      type: Array,
      default: () => [],
    },
    dataAnalytics: {
      type: Array,
      default: () => [],
    },
    userStats: {
      type: Object,
      default: () => {},
    },
    energy: Object,
    softSkills: Object,
    fatigueScore: Object,
    avgEi: Object,
    managementScale: Object,
  },
  setup(props)
  {
    const router = useRouter();
    const store = useStore();
    const permittedSubscriptions = computed(() => store.state.subscription.permittedSubscriptions);

    let eiLevels = [
      { from: 50, to: 70, label: 'Элементарный', class: 'low', },
      { from: 70, to: 90, label: 'Низкий', class: 'lowest',},
      { from: 90, to: 110, label: 'Средний', class: 'average', },
      { from: 110, to: 130, label: 'Высокий', class: 'high', },
      { from: 130, to: 150, label: 'Высший', class: 'highest' },
    ];

    let percentLevels = [
      { from: 0, to: 39, label: 'Низкий', class: 'low', },
      { from: 40, to: 79, label: 'Средний', class: 'average',},
      { from: 80, to: 100, label: 'Высокий', class: 'high', },
    ];

    let fatigueScoreLevels = [
      { from: 0, to: 3.9, label: 'Низкий', class: 'low', },
      { from: 4, to: 7.9, label: 'Средний', class: 'average',},
      { from: 8, to: 10, label: 'Высокий', class: 'high', },
    ];

    const getLevelByValue = (value, list) => {
      return list.find((level) => {
        return value >= level.from && value <= level.to;
      }) ?? null;
    };

    const getLevelLabel = (value, list) => {
      return getLevelByValue(value, list)?.label ?? '';
    };

    const getLevelClass = (value, list) => {
      return getLevelByValue(value, list)?.class ?? '';
    };

    const selectedReferenceProfileId = ref(null);
    const showDetailedAlignment = ref(false);

    const referenceProfileOptions = computed(() => {
      return props.referenceProfileAlignment.map(item => ({
        label: item.title,
        value: item.reference_profile_id,
      }));
    });

    const selectedUsers = computed(() => {
      const selectedProfile = props.referenceProfileAlignment.find(
          item => item.reference_profile_id === selectedReferenceProfileId.value
      );

      return selectedProfile?.users ?? [];
    });

    const handleLink = (link, type = null) => {
      if (type && type === 'blocked') {
        store.dispatch('users/setShowBlocked');
      }
      router.push(link);
    }

    const softSkillsChange = computed(() => {
      const total = props.softSkills?.total ?? 0;
      const previous = props.softSkills?.previous ?? 0;

      return total - previous;
    });

    const softSkillsText = computed(() => {
      if (softSkillsChange.value === null) return ''

      const sign = softSkillsChange.value > 0 ? '+' : ''

      return `${sign}${softSkillsChange.value}%`
    });

    const energyChange = computed(() => {
      const total = props.energy?.total ?? 0;
      const previous = props.energy?.previous ?? 0;

      return total - previous;
    });

    const energyChangeText = computed(() => {
      if (energyChange.value === null) return ''

      const sign = energyChange.value > 0 ? '+' : ''

      return `${sign}${energyChange.value}%`
    });

    const getPercentDiff = (current, previous, max = 10) => {
      const diff = current - previous;
      return Math.round((diff / max) * 100);
    };

    const fatigueChangePercent = computed(() => {
      if (props.fatigueScore?.total && props.fatigueScore?.previous) {
        return getPercentDiff(props.fatigueScore.total, props.fatigueScore.previous);
      } else {
        return 0;
      }
    });

    const fatigueChangeText = computed(() => {
      if (fatigueChangePercent.value === null) return ''

      const sign = fatigueChangePercent.value > 0 ? '+' : ''

      return `${sign}${fatigueChangePercent.value}%`
    });

    const companyTotalScore = computed(() => {
      return props.avgEi?.company_total ? Math.round(Number(props.avgEi?.company_total)) : 0;
    });

    const avgTotalScore = computed(() => {
      return props.avgEi?.country_total ? Math.round(Number(props.avgEi?.country_total)) : 0;
    });


    const toNumber = (value) => {
      const number = Number(value);

      return Number.isFinite(number) ? number : 0;
    };

    const getManagementValue = (key) => {
      return toNumber(props.managementScale?.[key]);
    };

    const getManagementDelta = (key) => {
      const deltaKey = `${key}_DELTA`;

      if (props.managementScale?.[deltaKey] === undefined || props.managementScale?.[deltaKey] === null) {
        return null;
      }

      return toNumber(props.managementScale[deltaKey]);
    };

    const getManagementDeltaClass = (key, delta) => {
      if (delta === null) {
        return 'is-neutral';
      }

      if (key === 'MODERATE') {
        return 'is-neutral';
      }

      if (key === 'HIGH') {
        if (delta > 0) return 'positive-change';
        if (delta < 0) return 'negative-change';

        return 'is-neutral';
      }

      if (key === 'OTHERS') {
        if (delta > 0) return 'negative-change';
        if (delta < 0) return 'positive-change';

        return 'is-neutral';
      }

      return 'is-neutral';
    };

    const managementItems = computed(() => {
      return [
        {
          key: 'HIGH',
          label: 'Высший и высокий',
          color: 'dark',
          value: getManagementValue('HIGH'),
          delta: getManagementDelta('HIGH'),
        },
        {
          key: 'MODERATE',
          label: 'Умеренный',
          color: 'medium',
          value: getManagementValue('MODERATE'),
          delta: getManagementDelta('MODERATE'),
        },
        {
          key: 'OTHERS',
          label: 'Остальные',
          color: 'light',
          value: getManagementValue('OTHERS'),
          delta: getManagementDelta('OTHERS'),
        },
      ].map((item) => {
        const delta = item.delta;

        return {
          ...item,
          deltaClass: getManagementDeltaClass(item.key, delta),
          deltaArrow: delta > 0 ? '↑' : delta < 0 ? '↓' : '–',
          deltaValue: delta !== null ? Math.abs(delta) : null,
        };
      });
    });

    const managementTotal = computed(() => {
      return managementItems.value.reduce((sum, item) => {
        return sum + item.value;
      }, 0);
    });

    const hasManagementSegments = computed(() => {
      return managementTotal.value > 0;
    });

    const managementSegments = computed(() => {
      if (!managementTotal.value) {
        return [];
      }

      return managementItems.value
          .filter(item => item.value > 0)
          .map(item => ({
            ...item,
            percent: Number(((item.value / managementTotal.value) * 100).toFixed(2)),
          }));
    });

    const isTablet = ref(window.innerWidth <= 1359)

    const onResize = () => {
      isTablet.value = window.innerWidth <= 1359
    }

    onMounted(() => {
      window.addEventListener('resize', onResize)
    });

    const hasSmallCardsAccess = computed(() => {
      return true;
    });

    watch(() => referenceProfileOptions.value,
        (options) => {
          if (!selectedReferenceProfileId.value && options.length) {
            selectedReferenceProfileId.value = options[0].value;
          }
        },
        { immediate: true }
    );

    return {
      router,
      referenceProfileOptions,
      selectedReferenceProfileId,
      selectedUsers,
      showDetailedAlignment,
      handleLink,
      energyChange,
      energyChangeText,
      percentLevels,
      getLevelClass,
      getLevelLabel,
      softSkillsChange,
      softSkillsText,
      getPercentDiff,
      fatigueChangePercent,
      fatigueChangeText,
      fatigueScoreLevels,
      companyTotalScore,
      avgTotalScore,
      eiLevels,
      managementItems,
      managementSegments,
      hasManagementSegments,
      isTablet,
      hasSmallCardsAccess
    }
  }
}
</script>

<style scoped lang="scss">
.blocks-container {
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  gap: 16px;
}

.content-block {
  flex: 1;
  background-color: #FFFFFF;
  padding: 20px;
  border-radius: 8px;
  color: rgba(0, 0, 0, 0.88);
  font-size: 14px;
  min-width: 320px;
  text-align: start;
}

.small-card {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 100px;
}

.status-header {
  display: flex;
  justify-content: space-between;
  column-gap: 10px;
  margin-bottom: 16px;
  font-size: 16px;
  font-weight: 600;
  color: var(--main-font);
}

.home-header {
  margin-bottom: 20px;
  font-size: 20px;
  text-align: left;
  font-weight: 500;
}

.progress-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 340px;
}

.progress-row {
  width: 100%;
  margin-bottom: 8px;
}

.progress-row__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 16px;
  line-height: 22px;
}

:deep(.ant-progress-line) {
  margin-bottom: 0 !important;
}

.progress-row__label {
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
}

.progress-row__value {
  color: var(--main-font);
  font-weight: 600;
  font-size: 14px;
  line-height: 120%;
}

.progress-row__bar :deep(.ant-progress-inner) {
  border-radius: 999px;
}

.progress-row__bar :deep(.ant-progress-bg) {
  border-radius: 999px;
}

.reference-profile-alignment {
  text-align: start;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  row-gap: 16px;
}

.alignment-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.alignment-item {
  display: flex;
  justify-content: space-between;
  row-gap: 8px;
  align-items: center;
}

.profile-name {
  color: var(--main-green);
}

.users-container {
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.users-row {
  display: flex;
  gap: 8px;
  justify-content: space-between;
  color: rgba(0, 0, 0, 0.65);
}

.count {
  color: var(--main-font);
  font-weight: 700;
}

.count-underlined {
  text-decoration: underline;
  cursor: pointer;
}

.dashboard-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.dashboard-summary-cards {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: start;
}

.dashboard-summary-cards :deep(.ant-card-body) {
  padding: 0;
}

.dashboard-summary-cards :deep(.small-card-wrapper__body) {
  align-items: stretch;
}

.metric-card {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.metric-card__main {
  margin-bottom: 4px;
  text-align: start;
  font-weight: 600;
  font-size: 24px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.metric-card__change {
  display: flex;
  gap: 4px;
  font-size: 14px;
  line-height: 120%;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.65);
}

.metric-card--energy .metric-card__change {
  margin-bottom: 10px;
}

.positive-change {
  color: var(--main-green);
}

.negative-change {
  color: #F55565;
}

.metric-card__progress {
  line-height: 1;
}

.metric-card__progress :deep(.ant-progress-inner) {
  border-radius: 999px;
}

.metric-card__progress :deep(.ant-progress-bg) {
  border-radius: 999px;
}

.metric-card__progress--gradient :deep(.ant-progress-bg) {
  background: linear-gradient(
          90deg,
          #ff6b6b 0%,
          #fadb14 45%,
          #8bd05b 75%,
          #21ac0c 100%
  ) !important;
}

.metric-card__scale {
  display: flex;
  justify-content: space-between;
  margin-top: 4px;
  font-size: 11px;
  line-height: 1.2;
  color: rgba(0, 0, 0, 0.45);
}

.company-ei-card {
  gap: 8px;
}

.company-ei-card__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.company-ei-card__scale {
  display: flex;
  justify-content: space-between;
  font-weight: 400;
  font-size: 12px;
  line-height: 110%;
  color: rgba(0, 0, 0, 0.45);
  text-transform: lowercase;
}

.ei-card-score {
  font-weight: 600;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.management-card {
  gap: 12px;
}

.management-card__legend {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.management-card__legend-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 42px 32px;
  align-items: center;
  column-gap: 8px;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.management-card__level-wrapper {
  display: flex;
  gap: 8px;
  align-items: center;
  min-width: 0;
}

.management-card__level-wrapper span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.management-card__mark {
  flex: 0 0 auto;
  width: 10px;
  height: 10px;
  border-radius: 2px;
}

.management-card__mark--dark {
  background: #21ac0c;
}

.management-card__mark--medium {
  background: #99da6e;
}

.management-card__mark--light {
  background: #d5dbe2;
}

.management-card__delta {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  white-space: nowrap;
}

.management-card__arrow {
  position: relative;
  top: -1px;
  font-size: 10px;
  line-height: 1;
  font-weight: 700;
}

.management-card__delta.is-neutral {
  color: rgba(0, 0, 0, 0.65);
}

.management-card__chart {
  width: 100%;
}

.management-card__bar {
  display: flex;
  width: 100%;
  height: 7px;
  gap: 2px;
  overflow: hidden;
  border-radius: 999px;
}

.management-card__bar--empty {
  margin-bottom: 18px;
  background: #d9dee7;
}

.management-card__bar-part {
  min-width: 0;
  height: 100%;
}

.management-card__bar-part--light {
  background: #d9dee7;
}

.management-card__bar-part--medium {
  background: #8bd05b;
}

.management-card__bar-part--dark {
  background: #21ac0c;
}

.management-card__numbers {
  margin-top: 4px;
  display: flex;
  width: 100%;
  gap: 2px;
  font-weight: 400;
  font-size: 12px;
  line-height: 110%;
  color: rgba(0, 0, 0, 0.45);
}

.management-card__number {
  min-width: 0;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fatigue-card__value {
  display: flex;
  gap: 4px;
  margin-bottom: 4px;
  font-weight: 600;
  font-size: 24px;
  line-height: 120%;
  display: flex;
  align-items: center;

  color: rgba(0, 0, 0, 0.88);

}

.fatigue-card__value .fatigue-current {
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.fatigue-card__value span {
  font-weight: 600;
  font-size: 24px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.45);
}

.users-card {
  justify-content: space-between;
}

.users-card__total {
  align-self: flex-end;
  margin-top: -2px;
  font-size: 14px;
  line-height: 1.2;
}

.users-card__rows {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.users-card__row {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 14px;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.65);
}

@media (max-width: $tablet-breakpoint) {
  .dashboard-summary-cards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: $mobile-breakpoint) {
  .dashboard-summary-cards {
    grid-template-columns: 1fr;
  }
}

.dashboard-summary-cards > * {
  display: flex;
}

.dashboard-summary-cards > * :deep(.ant-card-body) {
  width: 100%;
}

.dashboard-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 16px;
}

.dashboard-card {
  min-width: 0;
}

.dashboard-card--summary,
.dashboard-card--small {
  grid-column: span 2;
}

.dashboard-card--detail {
  grid-column: span 3;
}

.dashboard-card--energy {
  order: 1;
}

.dashboard-card--company-ei {
  order: 2;
}

.dashboard-card--management {
  order: 3;
}

.dashboard-card--soft-skills {
  order: 4;
}

.dashboard-card--fatigue {
  order: 5;
}

.dashboard-card--users {
  order: 6;
}

.dashboard-card--analytics {
  order: 7;
}

.dashboard-card--alignment {
  order: 8;
}

.dashboard-grid--subscription-locked {
  .dashboard-card--soft-skills {
    order: 1;
  }

  .dashboard-card--fatigue {
    order: 2;
  }

  .dashboard-card--users {
    order: 3;
  }

  .dashboard-card--analytics {
    order: 4;
  }

  .dashboard-card--alignment {
    order: 5;
  }

  .dashboard-card--energy {
    order: 6;
  }

  .dashboard-card--company-ei {
    order: 7;
  }

  .dashboard-card--management {
    order: 8;
  }
}

.dashboard-grid :deep(.ant-card-body) {
  padding: 0;
}

.dashboard-grid :deep(.small-card-wrapper__body) {
  align-items: stretch;
}

.dashboard-card :deep(.ant-card-body) {
  width: 100%;
}

.dashboard-card--users {
  display: flex;
}

.dashboard-card--users .small-card {
  width: 100%;
}

@media (max-width: $tablet-breakpoint) {
  .dashboard-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .dashboard-card--summary,
  .dashboard-card--small,
  .dashboard-card--detail {
    grid-column: span 1;
  }

  .dashboard-grid--subscription-locked,
  .dashboard-grid {
    .dashboard-card--energy {
      order: 1;
    }

    .dashboard-card--company-ei {
      order: 2;
    }

    .dashboard-card--management {
      order: 3;
    }

    .dashboard-card--soft-skills {
      order: 4;
    }

    .dashboard-card--fatigue {
      order: 5;
    }

    .dashboard-card--users {
      order: 6;
    }

    .dashboard-card--analytics {
      order: 7;
    }

    .dashboard-card--alignment {
      order: 8;
    }
  }
}

@media (max-width: $mobile-breakpoint) {
  .dashboard-grid {
    grid-template-columns: 1fr;
  }

  .dashboard-card--summary,
  .dashboard-card--small,
  .dashboard-card--detail {
    grid-column: span 1;
  }
}
</style>